import { cva } from 'class-variance-authority';
import { cn } from '@mokoboko/components';
import type { DayButtonProps } from 'react-day-picker';

type DayButtonState =
  | 'selected'
  | 'rangeStart'
  | 'rangeEnd'
  | 'rangeMiddle'
  | 'todayAvailable'
  | 'available'
  | 'today'
  | 'default';

function resolveState(modifiers: DayButtonProps['modifiers']): DayButtonState {
  if (modifiers.disabled) return 'default';
  if (modifiers.range_start) return 'rangeStart';
  if (modifiers.range_end) return 'rangeEnd';
  if (modifiers.range_middle) return 'rangeMiddle';
  if (modifiers.selected) return 'selected';
  if (modifiers.today && modifiers.available) return 'todayAvailable';
  if (modifiers.available) return 'available';
  if (modifiers.today) return 'today';
  return 'default';
}

const dayButtonVariants = cva(
  'flex items-center justify-center w-full h-11 rounded-10 text-sm leading-5 transition-[background-color,color,box-shadow,transform] duration-200 ease-out select-none outline-none',
  {
    variants: {
      state: {
        selected:
          'bg-red-500 text-on-brand font-semibold cursor-pointer shadow-xs active:scale-[0.97] active:duration-75 focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-2',
        rangeStart:
          'bg-red-500 text-on-brand font-semibold cursor-pointer rounded-r-none shadow-xs focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-2',
        rangeEnd:
          'bg-red-500 text-on-brand font-semibold cursor-pointer rounded-l-none shadow-xs focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-2',
        rangeMiddle:
          'bg-red-100 text-red-700 font-medium cursor-pointer rounded-none hover:bg-red-200 focus-visible:ring-2 focus-visible:ring-red-300',
        todayAvailable:
          'bg-red-50 text-red-500 font-semibold cursor-pointer shadow-skeu-secondary-red hover:bg-white hover:shadow-skeu-secondary-red-hover active:scale-[0.97] active:duration-75 focus-visible:ring-2 focus-visible:ring-red-300',
        available:
          'bg-red-50 text-red-500 font-semibold cursor-pointer shadow-skeu-secondary-red hover:bg-white hover:shadow-skeu-secondary-red-hover active:scale-[0.97] active:duration-75 focus-visible:ring-2 focus-visible:ring-red-300',
        today:
          'text-gray-900 font-semibold cursor-default ring-1 ring-inset ring-gray-200 focus-visible:ring-2 focus-visible:ring-gray-300',
        default:
          'text-gray-700 cursor-default focus-visible:ring-2 focus-visible:ring-gray-300',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

function DayButton({
  day,
  modifiers,
  className,
  ...props
}: DayButtonProps) {
  if (modifiers.outside) {
    return <div className="w-full h-11 rounded-10" aria-hidden="true" />;
  }

  const state = resolveState(modifiers);

  return (
    <button
      type="button"
      className={cn(dayButtonVariants({ state }), className)}
      {...props}
    />
  );
}

DayButton.displayName = 'DayButton';

export { DayButton, dayButtonVariants, resolveState };
export type { DayButtonState };
